import { useEffect, useRef, useState } from 'react';
import { Key, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGenerateKey } from '@/hooks/useKeys';
import { useAccessPolicy } from '@/hooks/useAccessPolicy';
import { AccessGateFlow, type GateTokens } from './AccessGateFlow';

interface GenerateKeyButtonProps {
  deviceId: string;
  deviceName: string;
  /**
   * Auto-läge (anläggningsdörrar): nyckeln hämtas direkt utan knapp — att ha
   * åtkomst till anläggningen ÄR att ha nycklarna. Grindade flöden
   * (verifiering/betalning/bokning) förblir explicita via knappen, och
   * backend-grinden i generate-key gäller oförändrat.
   */
  auto?: boolean;
  onKeyGenerated?: () => void;
}

export function GenerateKeyButton({ deviceId, deviceName, auto, onKeyGenerated }: GenerateKeyButtonProps) {
  const generateKey = useGenerateKey();
  const { data: policy, isFetched: policyFetched } = useAccessPolicy(deviceId);
  const [showGateFlow, setShowGateFlow] = useState(false);
  const autoAttempted = useRef(false);

  const needsGates = policy && (
    (policy.verification_required && policy.verification_required !== 'none') ||
    (policy.payment_required && policy.payment_required !== 'none' && policy.payment_required !== 'postpay') ||
    policy.booking_required === 'required'
  );

  const handleGenerate = async (gateTokens?: GateTokens) => {
    try {
      await generateKey.mutateAsync({
        device_id: deviceId,
        ...gateTokens,
      });
      setShowGateFlow(false);
      onKeyGenerated?.();
    } catch {
      // Error handled by mutation state
    }
  };

  const handleClick = () => {
    if (needsGates) {
      setShowGateFlow(true);
    } else {
      handleGenerate();
    }
  };

  // Auto-läge: hämta nyckeln så fort policyn är känd och ingen grind krävs.
  // Ett försök per montering — vid fel visas en "Hämta nyckel"-knapp som
  // fallback i stället för en tyst retry-loop.
  useEffect(() => {
    if (!auto || !policyFetched || needsGates || autoAttempted.current) return;
    autoAttempted.current = true;
    handleGenerate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auto, policyFetched, needsGates]);

  const handleGateComplete = (tokens: GateTokens) => {
    handleGenerate(tokens);
  };

  if (!deviceId) return null;

  // Auto-läge utan grindar: ingen "skapa nyckel"-knapp — bara hämtningsstatus.
  // Vid fel visas en explicit "Hämta nyckel"-fallback så dörren inte blir låst
  // i UI:t. Grindade dörrar faller igenom till det ordinarie knappflödet.
  if (auto && !needsGates) {
    if (generateKey.isError) {
      return (
        <div className="space-y-2">
          <Button
            size="lg"
            className="w-full"
            onClick={handleClick}
            disabled={generateKey.isPending}
          >
            <Key className="h-5 w-5" />
            Hämta nyckel — {deviceName}
          </Button>
          <p className="text-sm text-destructive text-center">
            {generateKey.error?.message || 'Kunde inte hämta nyckel'}
          </p>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center gap-2 py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Hämtar nyckel…</span>
      </div>
    );
  }

  if (showGateFlow && policy) {
    return (
      <div className="space-y-4">
        <AccessGateFlow
          deviceId={deviceId}
          policy={policy}
          onComplete={handleGateComplete}
          onCancel={() => setShowGateFlow(false)}
        />
        {generateKey.isPending && (
          <div className="flex items-center justify-center gap-2 py-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Genererar nyckel...</span>
          </div>
        )}
        {generateKey.isError && (
          <p className="text-sm text-destructive text-center">
            {generateKey.error?.message || 'Kunde inte generera nyckel'}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Button
        size="lg"
        className="w-full"
        onClick={handleClick}
        disabled={generateKey.isPending}
      >
        {generateKey.isPending ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <Key className="h-5 w-5" />
        )}
        Generera nyckel — {deviceName}
      </Button>
      {generateKey.isError && (
        <p className="text-sm text-destructive text-center">
          {generateKey.error?.message || 'Kunde inte generera nyckel'}
        </p>
      )}
    </div>
  );
}
